(() => {
  const pageRoot = document.querySelector('[data-alerts-page]');
  if (!pageRoot) return;

  const statusEl = pageRoot.querySelector('[data-alerts-page-status]');
  const noticeEl = pageRoot.querySelector('[data-alerts-page-notice]');
  const gridEl = pageRoot.querySelector('[data-alerts-waiting-grid]');
  const notifiedSectionEl = pageRoot.querySelector('[data-alerts-notified-section]');
  const notifiedGridEl = pageRoot.querySelector('[data-alerts-notified-grid]');
  const cardTemplate = document.querySelector('[data-alerts-card-template]');

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const formatMoney = (amount, currencyCode) => {
    if (!amount || !currencyCode) return '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(Number(amount));
    } catch (_error) {
      return `${amount} ${currencyCode}`;
    }
  };

  const normalizeVariantId = (variantId) => {
    const match = String(variantId || '').match(/\/ProductVariant\/(\d+)$/);
    return match ? match[1] : String(variantId || '').trim();
  };

  const parseJsonResponse = async (response) => {
    const raw = await response.text();
    try {
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  };

  const getInventoryState = (product) => {
    const quantity = Number(product.inventoryQuantity);
    const hasQuantity = Number.isFinite(quantity);
    const isInStock = hasQuantity ? quantity > 0 : Boolean(product.availableForSale);

    return {
      quantity: hasQuantity ? quantity : null,
      isInStock,
      label: hasQuantity
        ? (quantity > 0 ? `In stock: ${quantity}` : 'Out of stock')
        : (isInStock ? 'In stock' : 'Out of stock'),
    };
  };

  const renderEmptyState = (message = 'You do not have any active alerts yet.') => {
    gridEl.innerHTML = '';
    if (notifiedGridEl) notifiedGridEl.innerHTML = '';
    gridEl.classList.add('hidden');
    notifiedSectionEl?.classList.add('hidden');
    statusEl.classList.remove('hidden');
    statusEl.innerHTML = `
      <div class="rounded-[24px] border border-dotted border-[#c9d8e6] bg-white p-8 text-center shadow-[0_20px_60px_rgba(0,83,141,0.05)] md:p-12">
        <p class="mb-3 text-2xl text-[#202c3e]">${escapeHtml(message)}</p>
        <p class="mx-auto max-w-xl text-sm text-gray-500">
          Sign up on an out-of-stock product and it will show up here.
        </p>
      </div>
    `;
  };

  const buildProductCard = (product) => {
    if (!(cardTemplate instanceof HTMLTemplateElement)) {
      return null;
    }

    const fragment = cardTemplate.content.cloneNode(true);
    const article = fragment.querySelector('article');
    if (!article) return null;

    const links = article.querySelectorAll('[data-alerts-card-link], [data-alerts-card-title-link], [data-alerts-card-link-button]');
    const image = article.querySelector('[data-alerts-card-image]');
    const imagePlaceholder = article.querySelector('[data-alerts-card-image-placeholder]');
    const vendor = article.querySelector('[data-alerts-card-vendor]');
    const title = article.querySelector('[data-alerts-card-title]');
    const productCode = article.querySelector('[data-alerts-card-product-code]');
    const status = article.querySelector('[data-alerts-card-status]');
    const inventory = article.querySelector('[data-alerts-card-inventory]');
    const reviewBadge = article.querySelector('[data-alerts-card-review-badge]');
    const price = article.querySelector('[data-alerts-card-price]');
    const actionButton = article.querySelector('[data-alerts-card-link-button]');
    const inventoryState = getInventoryState(product);

    links.forEach((link) => {
      link.href = product.url || '#';
    });

    if (image) {
      if (product.imageUrl) {
        image.src = product.imageUrl;
        image.alt = product.imageAlt || product.title || 'Alert item';
        image.classList.remove('hidden');
        imagePlaceholder?.classList.add('hidden');
      } else {
        image.classList.add('hidden');
        imagePlaceholder?.classList.remove('hidden');
      }
    }

    if (vendor) {
      if (product.vendor) {
        vendor.textContent = product.vendor;
        vendor.classList.remove('hidden');
      } else {
        vendor.classList.add('hidden');
      }
    }

    if (title) {
      title.textContent = product.title || 'Untitled product';
    }

    if (productCode) {
      if (product.productCode) {
        productCode.textContent = `Product Code: ${product.productCode}`;
        productCode.classList.remove('hidden');
      } else {
        productCode.classList.add('hidden');
      }
    }

    if (status) {
      if (product.alertStatus === 'notified') {
        status.textContent = product.notifiedAt ? `Notified ${new Date(product.notifiedAt).toLocaleDateString()}` : 'Notified';
        status.classList.remove('hidden');
      } else {
        status.classList.add('hidden');
      }
    }

    if (inventory) {
      inventory.textContent = inventoryState.label;
      inventory.classList.remove('hidden');
    }

    if (reviewBadge) {
      if (product.reviewBadge) {
        reviewBadge.innerHTML = product.reviewBadge;
        reviewBadge.classList.remove('hidden');
      } else {
        reviewBadge.classList.add('hidden');
      }
    }

    if (price) {
      const formattedPrice = product.price ? formatMoney(product.price, product.currencyCode) : '';
      if (formattedPrice) {
        price.textContent = formattedPrice;
        price.classList.remove('hidden');
      } else {
        price.classList.add('hidden');
      }
    }

    if (actionButton) {
      actionButton.dataset.alertsAction = 'view';
      delete actionButton.dataset.variantId;
      delete actionButton.dataset.sku;
      actionButton.textContent = 'View product';
      actionButton.href = product.url || '#';

      if (product.alertStatus === 'notified' && inventoryState.isInStock) {
        actionButton.dataset.alertsAction = 'add-to-cart';
        actionButton.dataset.variantId = product.variantId || '';
        actionButton.textContent = 'Add to basket';
        actionButton.href = '#';
      } else if (product.alertStatus === 'notified') {
        actionButton.dataset.alertsAction = 'remind-again';
        actionButton.dataset.sku = product.sku || product.productCode || '';
        actionButton.textContent = 'Remind again';
        actionButton.href = '#';
      }
    }

    return article;
  };

  const renderProducts = (waitingProducts, notifiedProducts = []) => {
    if (!waitingProducts.length && !notifiedProducts.length) {
      renderEmptyState();
      return;
    }

    gridEl.innerHTML = '';
    const waitingCards = waitingProducts.map((product) => buildProductCard(product)).filter(Boolean);
    gridEl.append(...waitingCards);
    gridEl.classList.toggle('hidden', !waitingCards.length);

    if (notifiedGridEl) {
      notifiedGridEl.innerHTML = '';
      const notifiedCards = notifiedProducts.map((product) => buildProductCard(product)).filter(Boolean);
      notifiedGridEl.append(...notifiedCards);
      notifiedSectionEl?.classList.toggle('hidden', !notifiedCards.length);
    }

    statusEl.classList.add('hidden');
  };

  const fetchAlerts = async () => {
    const response = await fetch('/apps/wishlist/back-in-stock/items', {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    });

    const raw = await response.text();
    let payload = null;

    try {
      payload = raw ? JSON.parse(raw) : null;
    } catch (_error) {
      payload = null;
    }

    if (!payload) {
      throw new Error('Alerts route returned HTML instead of JSON. This usually means the Render route is not live yet or the app proxy is not resolving this path.');
    }

    if (response.status === 401) {
      renderEmptyState('Please sign in to view your saved alerts.');
      return;
    }

    if (!response.ok || payload.ok === false) {
      throw new Error(payload.error || `Failed to load alerts (${response.status})`);
    }

    renderProducts(
      Array.isArray(payload.waitingItems) ? payload.waitingItems : (Array.isArray(payload.items) ? payload.items : []),
      Array.isArray(payload.notifiedItems) ? payload.notifiedItems : [],
    );
  };

  const addToBasket = async (variantId) => {
    const normalizedVariantId = normalizeVariantId(variantId);
    if (!normalizedVariantId) {
      throw new Error('This product variant could not be added to the basket.');
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: normalizedVariantId,
        quantity: 1,
      }),
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok || payload?.status >= 400) {
      throw new Error(payload?.description || payload?.message || 'Could not add this item to the basket.');
    }

    return payload;
  };

  const RESUBSCRIBE_URL = 'https://bluerosepottery.us6.list-manage.com/subscribe?u=5ef565caa9&id=e0b4eb1915';

  const showNotice = (html) => {
    if (!noticeEl) return;
    noticeEl.innerHTML = html;
    noticeEl.classList.toggle('hidden', !html);
  };

  // The server reports whether the alert can actually be delivered. Restarting a
  // reminder for an address Mailchimp cannot reach looks identical to success unless
  // we say otherwise - which is the whole defect this page had.
  const noticeForDeliverability = (payload) => {
    if (payload?.deliverability === 'reachable' || payload?.deliverability === 'unknown') return '';

    if (payload?.deliverability === 'needs_confirmation') {
      return 'Your reminder is back on. Check your inbox and confirm your email so we can send the alert.';
    }

    if (payload?.deliverability !== 'unreachable') return '';

    if (payload?.deliverabilityStatus === 'cleaned') {
      return 'Your reminder is saved, but we could not deliver to this address. Please update the email on your account.';
    }

    if (payload?.deliverabilityStatus === 'archived') {
      return `Your reminder is saved, but this address was removed from our email list after a long gap, and only you can add it back. <a class="underline" target="_blank" rel="noopener" href="${RESUBSCRIBE_URL}">Re-subscribe here</a> and we'll send your alert as soon as it's back.`;
    }

    if (payload?.deliverabilityStatus === 'unsubscribed') {
      return `Your reminder is saved, but this address is unsubscribed from our emails, so we cannot send stock alerts to it. <a class="underline" target="_blank" rel="noopener" href="${RESUBSCRIBE_URL}">Re-subscribe here</a> to start receiving them again.`;
    }

    // Any status we cannot name. Make no claim about the cause.
    return `Your reminder is saved, but we cannot email this address right now. <a class="underline" target="_blank" rel="noopener" href="${RESUBSCRIBE_URL}">Re-subscribe here</a>, or update the email on your account.`;
  };

  const remindAgain = async (sku) => {
    if (!sku) {
      throw new Error('This alert is missing a SKU.');
    }

    const response = await fetch('/apps/wishlist/back-in-stock/remind-again', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ sku }),
    });
    const payload = await parseJsonResponse(response);

    if (!response.ok || payload?.ok === false) {
      throw new Error(payload?.error || 'Could not restart this reminder.');
    }

    return payload;
  };

  pageRoot.addEventListener('click', (event) => {
    const button = event.target.closest('[data-alerts-card-link-button]');
    if (!button || !pageRoot.contains(button)) return;

    const action = button.dataset.alertsAction || 'view';
    if (action === 'view') return;

    event.preventDefault();
    const originalText = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('pointer-events-none', 'opacity-70');

    const actionPromise = action === 'add-to-cart'
      ? addToBasket(button.dataset.variantId).then(() => {
        if (window.oaiq) oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: 'add-to-cart' });
        button.textContent = 'Added';
      })
      : remindAgain(button.dataset.sku).then((payload) => {
        showNotice(noticeForDeliverability(payload));
        return fetchAlerts();
      });

    button.textContent = action === 'add-to-cart' ? 'Adding...' : 'Saving...';

    void actionPromise
      .catch((error) => {
        button.textContent = originalText;
        window.alert(error.message || 'Please try again.');
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.classList.remove('pointer-events-none', 'opacity-70');
      });
  });

  document.addEventListener('DOMContentLoaded', () => {
    void fetchAlerts().catch((error) => {
      gridEl.innerHTML = '';
      gridEl.classList.add('hidden');
      statusEl.classList.remove('hidden');
      statusEl.innerHTML = `
        <div class="rounded-[24px] border border-dotted border-gray-300 bg-[#fafafa] p-8 text-center">
          <p class="mb-2 text-lg text-[#202c3e]">We couldn't load your alerts.</p>
          <p class="text-sm text-gray-500">${escapeHtml(error.message || 'Please try again.')}</p>
        </div>
      `;
    });
  });
})();
